import { createContext, useContext, useState, useRef } from "react";
import {
 ConfirmDialog,
 SelectDialog,
 InputDialog,
 NotificationsDialog,
 CheckoutDialog,
 RatingTypography,
 StatusChip,
} from "components";
import { Box, Typography, ListItem, ListItemText } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const DialogContext = createContext({
 confirm: () => Promise.reject(),
 select: () => Promise.reject(),
 input: () => Promise.reject(),
 history: () => Promise.reject(),
 openNotifications: () => {},
 openCheckout: () => {},
});

export function DialogProvider({ children }) {
 const [confirmDialogState, setConfirmDialogState] = useState({
  open: false,
  title: "",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  loading: false,
 });

 const [selectDialogState, setSelectDialogState] = useState({
  open: false,
  title: "",
  description: "",
  options: [],
  selectedValue: null,
  allowClickAwayClose: false,
  disableInteraction: false,
 });

 const [inputDialogState, setInputDialogState] = useState({
  open: false,
  title: "",
  description: "",
  fields: [],
  submitLabel: "Submit",
  cancelLabel: "Cancel",
 });

 const [notificationsOpen, setNotificationsOpen] = useState(false);
 const [checkoutOpen, setCheckoutOpen] = useState(false);

 const openNotifications = () => setNotificationsOpen(true);
 const openCheckout = () => setCheckoutOpen(true);

 const confirmResolveRef = useRef(null);
 const confirmRejectRef = useRef(null);
 const selectResolveRef = useRef(null);
 const selectRejectRef = useRef(null);
 const inputResolveRef = useRef(null);
 const inputRejectRef = useRef(null);

 const confirm = ({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
 }) => {
  return new Promise((resolve, reject) => {
   confirmResolveRef.current = resolve;
   confirmRejectRef.current = reject;

   setConfirmDialogState({
    open: true,
    title,
    description,
    confirmLabel,
    cancelLabel,
    loading: false,
   });
  });
 };

 const select = ({
  title,
  description,
  options = [],
  selectedValue = null,
  allowClickAwayClose = true,
  disableInteraction = false,
 }) => {
  return new Promise((resolve, reject) => {
   selectResolveRef.current = resolve;
   selectRejectRef.current = reject;

   setSelectDialogState({
    open: true,
    title,
    description,
    options,
    selectedValue,
    allowClickAwayClose,
    disableInteraction,
   });
  });
 };

 const input = ({
  title,
  description,
  fields = [],
  submitLabel = "Submit",
  cancelLabel = "Cancel",
 }) => {
  return new Promise((resolve, reject) => {
   inputResolveRef.current = resolve;
   inputRejectRef.current = reject;

   setInputDialogState({
    open: true,
    title,
    description,
    fields,
    submitLabel,
    cancelLabel,
   });
  });
 };

 const history = ({ review }) => {
  const historyOptions = (review.editHistory ?? []).map((entry, index) => {
   const rejectionEntry =
    entry.status === "rejected" && review.latestRejection?.reason
     ? { reason: review.latestRejection.reason }
     : null;

   return {
    label: (
     <ListItem dense disablePadding alignItems="flex-start">
      <ListItemText
       primary={
        <Box component="span">
         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
           sx={{ fontSize: 14 }}
           component="span"
           variant="caption"
           color="text.secondary"
          >
           {dayjs(entry.editedDate).fromNow()}
          </Typography>
          <StatusChip status={entry.status} />
         </Box>
         <RatingTypography rating={entry.previousRating} />
         <Typography variant="body2" mt={1}>
          {entry.previousDescription}
         </Typography>
         {entry.status === "rejected" && rejectionEntry?.reason && (
          <Typography
           variant="caption"
           color="error"
           sx={{ mt: 0.5, display: "block" }}
          >
           Reason: {rejectionEntry.reason}
          </Typography>
         )}
        </Box>
       }
      />
     </ListItem>
    ),
    value: index,
   };
  });

  const title = (
   <Box>
    <Typography variant="h6">Edit History</Typography>
    <Typography variant="subtitle1">
     {review.dealId ? review.dealTitle : review.restaurantName}
    </Typography>
    {review.dealId && (
     <Typography variant="body2" color="text.secondary">
      {review.restaurantName}
     </Typography>
    )}
   </Box>
  );

  return select({
   title,
   options: historyOptions,
   disableInteraction: true,
   allowClickAwayClose: true,
  });
 };

 const handleConfirm = async () => {
  if (!confirmResolveRef.current) return;

  setConfirmDialogState((prev) => ({ ...prev, loading: true }));

  try {
   confirmResolveRef.current(true);
   setConfirmDialogState((prev) => ({ ...prev, open: false, loading: false }));
  } catch (error) {
   setConfirmDialogState((prev) => ({ ...prev, loading: false }));
   throw error;
  }
 };

 const handleConfirmCancel = () => {
  if (confirmResolveRef.current) {
   confirmResolveRef.current(false);
  }
  setConfirmDialogState((prev) => ({ ...prev, open: false, loading: false }));
 };

 const handleConfirmClose = () => {
  if (!confirmDialogState.loading) {
   handleConfirmCancel();
  }
 };

 const handleSelect = (selectedOption) => {
  if (!selectResolveRef.current) return;

  selectResolveRef.current(selectedOption);
  setSelectDialogState((prev) => ({ ...prev, open: false }));
 };

 const handleSelectClose = () => {
  setSelectDialogState((prev) => ({ ...prev, open: false }));
 };

 const handleInputSubmit = async (values) => {
  if (!inputResolveRef.current) return;

  inputResolveRef.current(values);
  setInputDialogState((prev) => ({ ...prev, open: false }));
 };

 const handleInputClose = () => {
  setInputDialogState((prev) => ({ ...prev, open: false }));
 };

 return (
  <DialogContext.Provider
   value={{ confirm, select, input, history, openNotifications, openCheckout }}
  >
   {children}

   <ConfirmDialog
    open={confirmDialogState.open}
    title={confirmDialogState.title}
    description={confirmDialogState.description}
    confirmLabel={confirmDialogState.confirmLabel}
    cancelLabel={confirmDialogState.cancelLabel}
    onConfirm={handleConfirm}
    onClose={handleConfirmClose}
    loading={confirmDialogState.loading}
   />

   <SelectDialog
    open={selectDialogState.open}
    title={selectDialogState.title}
    description={selectDialogState.description}
    options={selectDialogState.options}
    selectedValue={selectDialogState.selectedValue}
    allowClickAwayClose={selectDialogState.allowClickAwayClose}
    disableInteraction={selectDialogState.disableInteraction}
    onSelect={handleSelect}
    onClose={handleSelectClose}
   />

   <InputDialog
    open={inputDialogState.open}
    title={inputDialogState.title}
    description={inputDialogState.description}
    fields={inputDialogState.fields}
    submitLabel={inputDialogState.submitLabel}
    cancelLabel={inputDialogState.cancelLabel}
    onSubmit={handleInputSubmit}
    onClose={handleInputClose}
   />

   <NotificationsDialog
    open={notificationsOpen}
    onClose={() => setNotificationsOpen(false)}
   />

   <CheckoutDialog
    open={checkoutOpen}
    onClose={() => setCheckoutOpen(false)}
   />
  </DialogContext.Provider>
 );
}

export const useDialog = () => {
 const context = useContext(DialogContext);

 if (!context) {
  throw new Error("useDialog must be used within a DialogProvider");
 }

 return context;
};
